import {
  DownloadIcon,
  FileIcon,
  PlusIcon,
  RedoIcon,
  RotateLeftIcon,
  RotateRightIcon,
  ScissorsIcon,
  TrashIcon,
  UndoIcon,
} from './Icons';

interface ToolbarProps {
  pageCount: number;
  selectedCount: number;
  outputCount: number;
  busy: boolean;
  onAddFiles: () => void;
  canUndo: boolean;
  canRedo: boolean;
  onUndo: () => void;
  onRedo: () => void;
  onSelectAll: () => void;
  onSelectNone: () => void;
  onRotateSelected: (delta: number) => void;
  onDeleteSelected: () => void;
  onInsertBlank: () => void;
  onSplitSelected: () => void;
  onSplitByDocument: () => void;
  onClearSplits: () => void;
  /** Whether any stamp is configured, so the button can say so. */
  stampsActive: boolean;
  onExportOptions: () => void;
  onExport: () => void;
}

export function Toolbar({
  pageCount,
  selectedCount,
  outputCount,
  busy,
  onAddFiles,
  canUndo,
  canRedo,
  onUndo,
  onRedo,
  onSelectAll,
  onSelectNone,
  onRotateSelected,
  onDeleteSelected,
  onInsertBlank,
  onSplitSelected,
  onSplitByDocument,
  onClearSplits,
  stampsActive,
  onExportOptions,
  onExport,
}: ToolbarProps) {
  const hasSelection = selectedCount > 0;

  return (
    <div className="toolbar">
      <button className="btn" onClick={onAddFiles} disabled={busy}>
        <PlusIcon /> Add files
      </button>

      <div className="toolbar__divider" />

      <button
        className="btn btn--sm btn--ghost"
        onClick={onUndo}
        disabled={!canUndo}
        title="Undo (Ctrl/⌘+Z)"
        aria-label="Undo"
      >
        <UndoIcon />
      </button>
      <button
        className="btn btn--sm btn--ghost"
        onClick={onRedo}
        disabled={!canRedo}
        title="Redo (Ctrl/⌘+Shift+Z)"
        aria-label="Redo"
      >
        <RedoIcon />
      </button>

      <div className="toolbar__divider" />

      <span className="toolbar__count">
        {pageCount} page{pageCount === 1 ? '' : 's'}
        {hasSelection ? ` · ${selectedCount} selected` : ''}
      </span>

      <button className="btn btn--sm btn--ghost" onClick={onSelectAll} disabled={pageCount === 0}>
        Select all
      </button>
      <button className="btn btn--sm btn--ghost" onClick={onSelectNone} disabled={!hasSelection}>
        Clear
      </button>

      <div className="toolbar__divider" />

      <button
        className="btn btn--sm"
        onClick={() => onRotateSelected(-90)}
        disabled={!hasSelection}
        title="Rotate selected pages left"
      >
        <RotateLeftIcon /> Left
      </button>
      <button
        className="btn btn--sm"
        onClick={() => onRotateSelected(90)}
        disabled={!hasSelection}
        title="Rotate selected pages right"
      >
        <RotateRightIcon /> Right
      </button>
      <button
        className="btn btn--sm btn--danger"
        onClick={onDeleteSelected}
        disabled={!hasSelection}
        title="Delete selected pages (Delete key)"
      >
        <TrashIcon /> Delete
      </button>

      <button
        className="btn btn--sm"
        onClick={onInsertBlank}
        title="Insert a blank page after the selection, or at the end"
      >
        <FileIcon /> Blank page
      </button>

      <div className="toolbar__divider" />

      <button
        className="btn btn--sm"
        onClick={onSplitSelected}
        disabled={!hasSelection}
        title="Start a new output file after each selected page"
      >
        <ScissorsIcon /> Split after
      </button>
      <button className="btn btn--sm btn--ghost" onClick={onSplitByDocument} disabled={pageCount === 0}>
        Split by source file
      </button>
      <button className="btn btn--sm btn--ghost" onClick={onClearSplits} disabled={outputCount <= 1}>
        Clear splits
      </button>

      <div className="toolbar__spacer" />

      <button
        className={stampsActive ? 'btn btn--sm btn--active' : 'btn btn--sm'}
        onClick={onExportOptions}
        disabled={pageCount === 0}
        title="Page numbers and watermarks"
      >
        Export options{stampsActive ? ' •' : ''}
      </button>

      <button className="btn btn--primary" onClick={onExport} disabled={pageCount === 0 || busy}>
        <DownloadIcon />
        {outputCount > 1 ? `Export ${outputCount} files` : 'Export PDF'}
      </button>
    </div>
  );
}
